import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { type ErrorHandler, Injector, PLATFORM_ID, Service, inject } from '@angular/core';

import { MessageService } from 'primeng/api';

import { TranslocoService } from '@jsverse/transloco';

import { ApiErrorResponse } from './api-error.model';
import { ERROR_LOG_ENABLED, logErrorDetail } from './error-log';
import { presentError } from './present-error';

/** The shortest gap between two "unexpected error" toasts, so a failing loop does not flood the screen. */
const TOAST_GAP_MS = 5_000;

/**
 * The app-wide `ErrorHandler`: an uncaught error is written to the console with its stack in
 * development builds only, and the user gets one translated line instead of nothing or a stack.
 * Failed calls are left to the flow that made them, which already says what went wrong.
 */
@Service()
export class AppErrorHandler implements ErrorHandler {
  private readonly _injector = inject(Injector);
  private readonly _logEnabled = inject(ERROR_LOG_ENABLED);
  private readonly _isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private _lastShownAt = Number.NEGATIVE_INFINITY;

  handleError(error: unknown): void {
    logErrorDetail(this._logEnabled, 'app', error);
    if (!this._isBrowser || isHttpFailure(error)) {
      return;
    }
    const now = Date.now();
    if (now - this._lastShownAt < TOAST_GAP_MS) {
      return;
    }
    this._lastShownAt = now;
    // Resolved lazily: the handler is built before the services it reports through. An app without
    // a toast host, or a toast that fails, must not turn the error handler into a second failure.
    const messages = this._injector.get(MessageService, null);
    const transloco = this._injector.get(TranslocoService, null);
    if (!messages || !transloco) {
      return;
    }
    try {
      messages.add({
        severity: 'error',
        icon: 'pi pi-times-circle',
        summary: transloco.translate('COMMON.ERROR_TITLE'),
        detail: transloco.translate(presentError(error).userKey),
      });
    } catch (toastError) {
      logErrorDetail(this._logEnabled, 'app', toastError);
    }
  }
}

/**
 * A failed call, as thrown directly, as an unhandled promise rejection (`rejection`) or as the
 * value read off a resource in error (`cause`).
 */
function isHttpFailure(error: unknown): boolean {
  const cause =
    error instanceof Error && 'rejection' in error
      ? error.rejection
      : error instanceof Error && error.cause !== undefined
        ? error.cause
        : error;
  return cause instanceof HttpErrorResponse || cause instanceof ApiErrorResponse;
}
